import axios from 'axios';

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
  timeout: 200000,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
});

api.interceptors.response.use((response) => {
  if (response?.data?.result !== 'SUCCESS') {
    throw response;
  } else {
    return response.data;
  }
}, (error) => {
  throw error;
});

export default {
  fetchHelloWorld() {
    return api
      .get('/');
  },

  getCompanyByClientId(clientId) {
    return api
      .get(`/companies/client/${clientId}`)
      .then((data) => data.company);
  },

  getCompanyByCode(companyCode) {
    return api
      .get(`/companies/code/${companyCode}`)
      .then((data) => data.company);
  },

  getGuestById(guestId) {
    return api
      .get(`/guests/${guestId}`)
      .then((data) => data.guest);
  },

  getGuestByReservationId(reservationId) {
    return api
      .get(`/guests/reservation/${reservationId}`)
      .then((data) => data.guest);
  },

  getGuestByReservation(companyCode, reservationCode) {
    return api
      .get(`/guests/${companyCode}/${reservationCode}`)
      .then((data) => data.guest);
  },

  getGuestByUniqueAttributes(firstName, lastName, phoneNumber, postalCode) {
    return api
      .get(`/guests/${firstName}/${lastName}/${phoneNumber}/${postalCode}`)
      .then((data) => data.guest);
  },

  getReservationById(reservationId) {
    return api
      .get(`/reservations/${reservationId}`)
      .then((data) => data.reservation);
  },

  getReservationByCode(companyCode, reservationCode) {
    return api
      .get(`/reservations/${companyCode}/${reservationCode}`)
      .then((data) => data.reservation);
  },

  getSummaryByCompanyCode(companyCode) {
    return api
      .get(`/reservations/summary/${companyCode}`)
      .then((data) => data.summary);
  },

  getReservationsByCompanyCode(companyCode) {
    return api
      .get(`/reservations/all/${companyCode}`)
      .then((data) => data.reservations);
  },

  getReservationByReference(reference) {
    return api
      .get(`/reservations/reference/${reference}`)
      .then((data) => data.reservation);
  },

  getPaymentCardSessionByReference(reference) {
    return api
      .get(`/verifications/card/${reference}`)
      .then((data) => data.session);
  },

  getPaymentCardSessionByIdentityReference(reference) {
    return api
      .get(`/verifications/card/identity/${reference}`)
      .then((data) => data.session);
  },

  getPaymentCardSessionByReservation(companyCode, reservationCode) {
    return api
      .get(`/verifications/card/${companyCode}/${reservationCode}`)
      .then((data) => data.session);
  },

  getPaymentCardSessionsByStatus(statusCode) {
    return api
      .get(`/verifications/card/status/${statusCode}`)
      .then((data) => data.sessions);
  },

  getPaymentCardImageByReference(reference) {
    return api
      .get(`/verifications/card/image/${reference}`)
      .then((data) => data.details
        && data.details.session
        && data.details.session.cardImages
        && (data.details.session.cardImages.edited || data.details.session.cardImages.original));
  },

  getPaymentCardBackupByReference(reference) {
    return api
      .get(`/verifications/card/backup/${reference}`)
      .then((data) => data.imageBackup);
  },

  createPaymentCardSession(companyCode, reservationCode) {
    return api
      .post('/verifications/card', { companyCode, reservationCode })
      .then((data) => data.session);
  },

  patchPaymentCardSession(card) {
    return api
      .patch('/verifications/card', card)
      .then((data) => data.session);
  },

  parsePaymentCard(companyCode, reservationCode, cardImage) {
    return api
      .post('/verifications/card/parse', { companyCode, reservationCode, cardImage })
      .then((data) => data.session);
  },

  saveEditedPaymentCard(reference, cardImage) {
    return api
      .post(`/verifications/card/image/edit/${reference}`, { cardImage })
      .then((data) => data);
  },

  getSessionByGuestId(guestId) {
    return api
      .get(`/verifications/identity/${guestId}`)
      .then((data) => data.session);
  },

  getSessionByReservation(companyCode, reservationCode) {
    return api
      .get(`/verifications/identity/${companyCode}/${reservationCode}`)
      .then((data) => data.session);
  },

  getSessionByReference(reference) {
    return api
      .get(`/verifications/identity/reference/${reference}`)
      .then((data) => data.session);
  },

  createVerificationSession(companyCode, reservationCode) {
    return api
      .post('/verifications/identity', { companyCode, reservationCode })
      .then((data) => data.session);
  },

  createNameAddressMatch(companyCode, reservationCode) {
    return api
      .post('/verifications/address', { companyCode, reservationCode })
      .then((data) => data.match);
  },

  getNameAddressMatchByGuestId(guestId) {
    return api
      .get(`/verifications/address/${guestId}`)
      .then((data) => data.match);
  },

  createRiskVerificationSession(companyCode, reservationCode) {
    return api
      .post('/verifications/risk', { companyCode, reservationCode })
      .then((data) => data.match);
  },

  getRiskVerificationByGuestId(guestId) {
    return api
      .get(`/verifications/risk/${guestId}`)
      .then((data) => data.match);
  },

  createBackgroundCheckSession(companyCode, reservationCode) {
    return api
      .post('/verifications/background', { companyCode, reservationCode })
      .then((data) => data.match);
  },

  getBackgroundCheckByGuestId(guestId) {
    return api
      .get(`/verifications/background/${guestId}`)
      .then((data) => data.match);
  },

  addGuest(newGuest) {
    return api
      .post('/guests', newGuest)
      .then((data) => data.guest);
  },

  updateGuest(guestId, guestData) {
    return api
      .put(`/guests/${guestId}`, guestData)
      .then((data) => data.guest);
  },

  addReservation(newReservation) {
    return api
      .post('/reservations', newReservation)
      .then((data) => data.reservation);
  },

  updateReservation(reservationId, reservationData) {
    return api
      .put(`/reservations/${reservationId}`, reservationData)
      .then((data) => data.reservation);
  },

  addReview(newReview) {
    return api
      .post('/reviews', newReview)
      .then((data) => data.review);
  },

  updateReview(reviewId, reviewData) {
    return api
      .put(`/reviews/${reviewId}`, reviewData)
      .then((data) => data.review);
  },

  getReviewById(reviewId) {
    return api
      .get(`/reviews/${reviewId}`)
      .then((data) => data.review);
  },

  validateReviewByGuestId(reviewId, guestId) {
    return api
      .get(`/reviews/validation/${reviewId}/${guestId}`)
      .then((data) => data.valid);
  },

  getReviewList(guestId) {
    return api
      .get(`/reviews/${guestId}/list`)
      .then((data) => data.reviews);
  },

  getReviewSummary(guestId) {
    return api
      .get(`/reviews/${guestId}/summary`)
      .then((data) => data.summary);
  },

  getVerificationList(companyCode) {
    return api
      .get(`/reservations/guests/${companyCode}`)
      .then((data) => data.reservations);
  },

  getRecentVerifications(companyCode) {
    return api
      .get(`/reservations/guests/${companyCode}/recent`)
      .then((data) => data.reservations);
  },

  generateLead(
    firstName,
    lastName,
    companyName,
    emailAddress,
    phoneNumber,
    notes,
  ) {
    return api
      .post('/leads', {
        firstName, lastName, companyName, emailAddress, phoneNumber, notes,
      })
      .then((data) => data.summary);
  },
};
